import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { type SessionStorage, sessionStorageManager } from '../tools/storageManager';
import useEventCallback from './useEventCallback';
import useEventListener from './useEventListener';

declare global {
  interface WindowEventMap {
    'session-storage': CustomEvent;
  }
}

type UseSessionStorageOptions<T extends keyof SessionStorage> = {
  serializer?: (value: SessionStorage[T]) => string;
  deserializer?: (value: string) => SessionStorage[T];
  initializeWithValue?: boolean;
};

const IS_SERVER = typeof window === 'undefined';

/**
 * sessionStorage-backed counterpart to {@link useLocalStorage}.
 *
 * Use this (not localStorage) for transient, tab-scoped UI state — e.g. an
 * in-progress wizard. sessionStorage survives page refresh, tab-switch,
 * window-focus refetches, and short idle periods within the open tab, but is
 * cleared when the tab closes and is never shared across tabs, so two tabs can't
 * clobber each other's drafts.
 */
export function useSessionStorage<K extends keyof SessionStorage>(
  key: K,
  initialValue: SessionStorage[K] | (() => SessionStorage[K]),
  options: UseSessionStorageOptions<K> = {},
): [SessionStorage[K], Dispatch<SetStateAction<SessionStorage[K]>>, () => void] {
  const { initializeWithValue = true, serializer, deserializer } = options;

  const readValue = useCallback((): SessionStorage[K] => {
    const initialValueToUse = initialValue instanceof Function ? initialValue() : initialValue;

    if (IS_SERVER) {
      return initialValueToUse;
    }

    try {
      const value = sessionStorageManager.get(key, deserializer);
      return value !== undefined ? value : initialValueToUse;
    } catch (error) {
      console.warn(`Error reading sessionStorage key “${key}”:`, error);
      return initialValueToUse;
    }
  }, [initialValue, key, deserializer]);

  const [storedValue, setStoredValue] = useState<SessionStorage[K]>(() => {
    return initializeWithValue
      ? readValue()
      : initialValue instanceof Function
        ? initialValue()
        : initialValue;
  });

  const setValue: Dispatch<SetStateAction<SessionStorage[K]>> = useEventCallback((value) => {
    if (IS_SERVER) {
      console.warn(
        `Tried setting sessionStorage key “${key}” even though environment is not a client`,
      );
      return;
    }

    try {
      const newValue = value instanceof Function ? value(storedValue) : value;
      sessionStorageManager.set(key, newValue, serializer);
      setStoredValue(newValue);
      window.dispatchEvent(new StorageEvent('session-storage', { key: key as string }));
    } catch (error) {
      console.warn(`Error setting sessionStorage key “${key}”:`, error);
    }
  });

  const removeValue = useEventCallback(() => {
    if (IS_SERVER) {
      console.warn(
        `Tried removing sessionStorage key “${key}” even though environment is not a client`,
      );
    }

    const defaultValue = initialValue instanceof Function ? initialValue() : initialValue;
    sessionStorageManager.remove(key as string);
    setStoredValue(defaultValue);
    window.dispatchEvent(new StorageEvent('session-storage', { key: key as string }));
  });

  useEffect(() => {
    setStoredValue(readValue());
  }, [key, readValue]);

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      if ((event as StorageEvent).key && (event as StorageEvent).key !== key) {
        return;
      }
      setStoredValue(readValue());
    },
    [key, readValue],
  );

  useEventListener('storage', handleStorageChange);
  useEventListener('session-storage', handleStorageChange);

  return [storedValue, setValue, removeValue];
}
